/**
 * ShadowWalkerEntity — a shadow creature that patrols corridors using
 * grid-based cell-to-cell movement (like the player), can detect the
 * player via line-of-sight, chase them using BFS pathfinding, steal
 * coins, and deal damage. After hitting, the shadow retreats briefly
 * and then returns to its spawn zone to resume patrolling.
 *
 * Chase behaviour is time-limited: after SHADOW_WALKER_CHASE_DURATION ms
 * of continuous chasing the shadow gives up, turns around and walks back
 * toward its spawn position. Once it arrives (or gets close enough) it
 * resumes random patrol — and can spot the player again later.
 *
 * States:
 *   - 'patrol'     : random corridor movement along PATH cells
 *   - 'chase'      : spotted player, BFS pathfinding toward them (max 15s)
 *   - 'retreating' : just hit the player, retreating a few cells away
 *   - 'returning'  : heading back to spawn zone after chase timeout / retreat
 *   - 'despawning' : fading out before destruction (only on explicit destroy)
 *
 * Shadow Walkers are NOT visible through fog — only when within the
 * player's fog reveal radius. They move strictly along PATH cells and
 * never pass through walls. Ghost mode (Agmuth) lets the player pass
 * through without taking damage.
 */

import Phaser from 'phaser';
import { TEX_SHADOW_WALKER, CellType } from '../utils/constants.js';
import {
  SHADOW_WALKER_SIZE,
  SHADOW_WALKER_PATROL_TWEEN_MS,
  SHADOW_WALKER_CHASE_TWEEN_MS,
  SHADOW_WALKER_VISION_RANGE,
  SHADOW_WALKER_DAMAGE,
  SHADOW_WALKER_COIN_STEAL,
  SHADOW_WALKER_DESPAWN_DURATION,
  SHADOW_WALKER_DIRECTION_CHANGE_INTERVAL,
  SHADOW_WALKER_HIT_COOLDOWN,
  SHADOW_WALKER_RETREAT_CELLS,
  SHADOW_WALKER_RETREAT_TWEEN_MS,
  SHADOW_WALKER_CHASE_DURATION,
  SHADOW_WALKER_RETURN_TWEEN_MS,
  TILE_SIZE,
  AUDIO_SHADOW_WALKER_SPOT,
  AUDIO_SHADOW_WALKER_HIT,
  AUDIO_SHADOW_WALKER_DESPAWN,
  COLOR_SHADOW_WALKER_EYES,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/** @enum {string} */
const ShadowState = Object.freeze({
  PATROL: 'patrol',
  CHASE: 'chase',
  RETREATING: 'retreating',
  RETURNING: 'returning',
  DESPAWNING: 'despawning',
});

/** Cardinal direction deltas on the expanded grid. */
const CARDINAL = Object.freeze([
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
]);

/** Manhattan distance (in expanded-grid cells) considered "close enough" to spawn. */
const RETURN_ARRIVE_DIST = 3;

export default class ShadowWalkerEntity extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} col           - Starting grid column (expanded).
   * @param {number} row           - Starting grid row (expanded).
   * @param {number[][]} grid      - Maze grid reference.
   * @param {object} playerRef     - Player sprite reference { x, y, gridCol, gridRow }.
   */
  constructor(scene, col, row, grid, playerRef) {
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    super(scene, x, y, TEX_SHADOW_WALKER);

    this.scene = scene;
    this._grid = grid;
    this._playerRef = playerRef;
    this._state = ShadowState.PATROL;

    /** @type {number} Current expanded grid column. */
    this.gridCol = col;
    /** @type {number} Current expanded grid row. */
    this.gridRow = row;

    /** @type {number} Spawn column — where the shadow was placed at level start. */
    this._spawnCol = col;
    /** @type {number} Spawn row — where the shadow was placed at level start. */
    this._spawnRow = row;

    /** @type {boolean} Whether the entity is mid-tween between cells. */
    this._isMoving = false;

    /** @type {{ dx: number, dy: number }} Last patrol direction for corridor following. */
    this._lastDir = CARDINAL[Math.floor(Math.random() * 4)];

    /** @type {number} Accumulated ms without a direction change event. */
    this._dirChangeAccum = 0;

    /** @type {boolean} Whether the shadow has spotted the player (ever, for first-spot sound). */
    this._hasSpotted = false;

    /** @type {Array<{col:number,row:number}>|null} BFS path (chase / return). */
    this._chasePath = null;

    /** @type {number} Accumulated ms since last BFS recompute. */
    this._chasePathAge = 0;

    /** @type {number} Accumulated ms since losing LOS during chase. */
    this._losLostAccum = 0;

    /** @type {number} Accumulated ms spent in CHASE state this engagement. */
    this._chaseAccum = 0;

    // --- Hit cooldown ---
    /** @type {number} Remaining cooldown ms before the shadow can hit again. */
    this._hitCooldown = 0;

    /** @type {number} Cells remaining to retreat after hitting. */
    this._retreatCellsLeft = 0;

    // --- Return path ---
    /** @type {Array<{col:number,row:number}>|null} BFS path back to spawn. */
    this._returnPath = null;

    /** @type {number} Accumulated ms since last return-path BFS recompute. */
    this._returnPathAge = 0;

    // Floating bob parameters
    this._bobOffset = Math.random() * Math.PI * 2;
    this._bobTimer = 0;

    // Eye glow graphics
    this._eyeGfx = scene.add.graphics();
    this._eyeGfx.setDepth(201);

    // Configure sprite
    scene.add.existing(this);
    this.setDepth(200);
    this.setAlpha(0.75);
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /** @returns {string} Current shadow AI state. */
  get shadowState() {
    return this._state;
  }

  /** @returns {boolean} True if active and not despawning. */
  get isActive() {
    return this.active && this._state !== ShadowState.DESPAWNING;
  }

  /**
   * Main update tick — called by ShadowWalkerSystem each frame.
   * @param {number} delta - ms since last frame.
   * @param {boolean} isGhostMode - Whether the player is in Agmuth ghost mode.
   * @returns {{ hit: boolean, damage: number, coinsStolen: number }}
   */
  tick(delta, isGhostMode) {
    if (!this.active || !this.scene) return { hit: false, damage: 0, coinsStolen: 0 };

    // Floating bob animation
    this._bobTimer += delta;

    // Tick hit cooldown
    if (this._hitCooldown > 0) {
      this._hitCooldown -= delta;
      if (this._hitCooldown < 0) this._hitCooldown = 0;
    }

    switch (this._state) {
      case ShadowState.PATROL:
        return this._updatePatrol(delta, isGhostMode);
      case ShadowState.CHASE:
        return this._updateChase(delta, isGhostMode);
      case ShadowState.RETREATING:
        return this._updateRetreat(delta);
      case ShadowState.RETURNING:
        this._updateReturn(delta);
        return { hit: false, damage: 0, coinsStolen: 0 };
      default:
        return { hit: false, damage: 0, coinsStolen: 0 };
    }
  }

  /** Begin fade-out and self-destruct. */
  despawn() {
    if (this._state === ShadowState.DESPAWNING) return;
    this._state = ShadowState.DESPAWNING;

    try {
      this.scene.sound.play(AUDIO_SHADOW_WALKER_DESPAWN, { volume: getSfxVol(0.3) });
    } catch (_) { /* audio fallback */ }

    if (!this.scene) {
      this._cleanup();
      this.destroy();
      return;
    }

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      duration: SHADOW_WALKER_DESPAWN_DURATION,
      ease: 'Quad.easeIn',
      onUpdate: () => this._drawEyes(),
      onComplete: () => {
        this._cleanup();
        this.destroy();
      },
    });
  }

  /**
   * Clean up eye graphics.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    this._cleanup();
    super.destroy(fromScene);
  }

  // ---------------------------------------------------------------
  // Private — State updates
  // ---------------------------------------------------------------

  /**
   * Patrol: move cell-by-cell along corridors, preferring straight
   * lines and occasionally changing direction.
   * @private
   */
  _updatePatrol(delta, isGhostMode) {
    // Check LOS before moving — may switch to chase
    if (this._checkLineOfSight()) {
      this._enterChase();
      return this._checkPlayerContact(isGhostMode);
    }

    // If still tweening, just wait
    if (this._isMoving) {
      this._drawEyes();
      return this._checkPlayerContact(isGhostMode);
    }

    this._dirChangeAccum += delta;

    // Occasionally pick a fully random direction
    if (this._dirChangeAccum >= SHADOW_WALKER_DIRECTION_CHANGE_INTERVAL) {
      this._dirChangeAccum = 0;
      this._lastDir = CARDINAL[Math.floor(Math.random() * 4)];
    }

    // Try to continue in current direction; if blocked, pick random walkable
    const fwdCol = this.gridCol + this._lastDir.dx;
    const fwdRow = this.gridRow + this._lastDir.dy;

    if (this._isWalkable(fwdCol, fwdRow)) {
      this._tweenToCell(fwdCol, fwdRow, SHADOW_WALKER_PATROL_TWEEN_MS);
    } else {
      // Gather all walkable neighbours
      const neighbours = this._getWalkableNeighbours(this.gridCol, this.gridRow);
      if (neighbours.length > 0) {
        const pick = neighbours[Math.floor(Math.random() * neighbours.length)];
        this._lastDir = { dx: pick.col - this.gridCol, dy: pick.row - this.gridRow };
        this._tweenToCell(pick.col, pick.row, SHADOW_WALKER_PATROL_TWEEN_MS);
      }
      // else: stuck — do nothing this frame
    }

    this._drawEyes();
    return this._checkPlayerContact(isGhostMode);
  }

  /**
   * Chase: BFS toward the player, cell by cell. Time-limited by
   * SHADOW_WALKER_CHASE_DURATION — after that the shadow gives up
   * and returns to its spawn zone.
   * @private
   */
  _updateChase(delta, isGhostMode) {
    if (!this._playerRef) return { hit: false, damage: 0, coinsStolen: 0 };

    // Accumulate chase time
    this._chaseAccum += delta;

    // Chase timeout — give up and return home
    if (this._chaseAccum >= SHADOW_WALKER_CHASE_DURATION) {
      this._enterReturning();
      return { hit: false, damage: 0, coinsStolen: 0 };
    }

    // Recompute BFS path periodically or when stale
    this._chasePathAge += delta;
    if (!this._chasePath || this._chasePath.length === 0 || this._chasePathAge > 500) {
      this._chasePath = this._bfsPath(
        this.gridCol, this.gridRow,
        this._playerRef.gridCol, this._playerRef.gridRow,
      );
      this._chasePathAge = 0;
    }

    // If still tweening, just wait
    if (this._isMoving) {
      this._drawEyes();
      return this._checkPlayerContact(isGhostMode);
    }

    // Follow BFS path
    if (this._chasePath && this._chasePath.length > 0) {
      const next = this._chasePath.shift();
      this._lastDir = { dx: next.col - this.gridCol, dy: next.row - this.gridRow };
      this._tweenToCell(next.col, next.row, SHADOW_WALKER_CHASE_TWEEN_MS);
    } else {
      // No path found — fall back to cardinal approach
      this._moveTowardPlayerCardinal();
    }

    // If LOS lost for too long, return home
    if (!this._checkLineOfSight()) {
      this._losLostAccum += delta;
      if (this._losLostAccum > 3000) {
        this._enterReturning();
        return { hit: false, damage: 0, coinsStolen: 0 };
      }
    } else {
      this._losLostAccum = 0;
    }

    this._drawEyes();
    return this._checkPlayerContact(isGhostMode);
  }

  /**
   * Retreat: move away from the player for a few cells after landing a hit,
   * then switch to RETURNING to head back to spawn.
   * @private
   */
  _updateRetreat(delta) {
    this._drawEyes();

    // If still tweening, just wait
    if (this._isMoving) return { hit: false, damage: 0, coinsStolen: 0 };

    // If retreat distance reached, head home
    if (this._retreatCellsLeft <= 0) {
      this._enterReturning();
      return { hit: false, damage: 0, coinsStolen: 0 };
    }

    // Move to the walkable neighbour furthest from the player
    const bestCell = this._getFurthestNeighbourFromPlayer();
    if (bestCell) {
      this._lastDir = { dx: bestCell.col - this.gridCol, dy: bestCell.row - this.gridRow };
      this._tweenToCell(bestCell.col, bestCell.row, SHADOW_WALKER_RETREAT_TWEEN_MS);
      this._retreatCellsLeft--;
    } else {
      // Stuck, can't retreat further — head home
      this._retreatCellsLeft = 0;
      this._enterReturning();
    }

    return { hit: false, damage: 0, coinsStolen: 0 };
  }

  /**
   * Returning: BFS back toward the spawn position. Once close enough,
   * switch to patrol. Ignores the player during this phase — the shadow
   * is disengaging and heading home.
   * @private
   */
  _updateReturn(delta) {
    this._drawEyes();

    // Check if close enough to spawn to resume patrol
    const distToSpawn = Math.abs(this.gridCol - this._spawnCol) +
                        Math.abs(this.gridRow - this._spawnRow);
    if (distToSpawn <= RETURN_ARRIVE_DIST) {
      this._enterPatrol();
      return;
    }

    // If still tweening, just wait
    if (this._isMoving) return;

    // Recompute return path periodically
    this._returnPathAge += delta;
    if (!this._returnPath || this._returnPath.length === 0 || this._returnPathAge > 800) {
      this._returnPath = this._bfsPath(
        this.gridCol, this.gridRow,
        this._spawnCol, this._spawnRow,
      );
      this._returnPathAge = 0;
    }

    // Follow return path
    if (this._returnPath && this._returnPath.length > 0) {
      const next = this._returnPath.shift();
      this._lastDir = { dx: next.col - this.gridCol, dy: next.row - this.gridRow };
      this._tweenToCell(next.col, next.row, SHADOW_WALKER_RETURN_TWEEN_MS);
    } else {
      // BFS couldn't find a path (spawn may be unreachable) — just patrol here
      this._enterPatrol();
    }
  }

  // ---------------------------------------------------------------
  // Private — State transitions
  // ---------------------------------------------------------------

  /**
   * Transition into CHASE state, resetting the chase timer.
   * @private
   */
  _enterChase() {
    this._state = ShadowState.CHASE;
    this._chaseAccum = 0;
    this._chasePath = null;
    this._chasePathAge = 9999; // force immediate BFS
    this._losLostAccum = 0;

    if (!this._hasSpotted) {
      this._hasSpotted = true;
      try {
        this.scene.sound.play(AUDIO_SHADOW_WALKER_SPOT, { volume: getSfxVol(0.4) });
      } catch (_) { /* audio fallback */ }
    }
  }

  /**
   * Transition into RETURNING state — head back to spawn zone.
   * @private
   */
  _enterReturning() {
    this._state = ShadowState.RETURNING;
    this._returnPath = null;
    this._returnPathAge = 9999; // force immediate BFS
    this._chasePath = null;
    this._chaseAccum = 0;
    this._losLostAccum = 0;
    // Allow re-spotting the player on the next patrol
    this._hasSpotted = false;
  }

  /**
   * Transition into PATROL state from returning.
   * @private
   */
  _enterPatrol() {
    this._state = ShadowState.PATROL;
    this._chasePath = null;
    this._returnPath = null;
    this._chaseAccum = 0;
    this._losLostAccum = 0;
    this._dirChangeAccum = 0;
    this._lastDir = CARDINAL[Math.floor(Math.random() * 4)];
    this._hasSpotted = false;
  }

  // ---------------------------------------------------------------
  // Private — Movement
  // ---------------------------------------------------------------

  /**
   * Tween the sprite from its current cell to the target cell.
   * @private
   * @param {number} col
   * @param {number} row
   * @param {number} duration — ms for the tween
   */
  _tweenToCell(col, row, duration) {
    if (this._isMoving || !this.scene) return;

    this._isMoving = true;

    const targetX = col * TILE_SIZE + TILE_SIZE / 2;
    const targetY = row * TILE_SIZE + TILE_SIZE / 2;

    this.scene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration,
      ease: 'Sine.easeInOut',
      onUpdate: () => this._drawEyes(),
      onComplete: () => {
        this.gridCol = col;
        this.gridRow = row;
        this._isMoving = false;
      },
    });
  }

  /**
   * Try to move one cell closer to the player using cardinal directions.
   * Fallback for when BFS returns no path.
   * @private
   */
  _moveTowardPlayerCardinal() {
    if (!this._playerRef || this._isMoving) return;

    const dx = this._playerRef.gridCol - this.gridCol;
    const dy = this._playerRef.gridRow - this.gridRow;

    // Prefer the axis with the larger gap
    const tryOrders = Math.abs(dx) >= Math.abs(dy)
      ? [{ dx: Math.sign(dx), dy: 0 }, { dx: 0, dy: Math.sign(dy) }]
      : [{ dx: 0, dy: Math.sign(dy) }, { dx: Math.sign(dx), dy: 0 }];

    for (const d of tryOrders) {
      if (d.dx === 0 && d.dy === 0) continue;
      const nc = this.gridCol + d.dx;
      const nr = this.gridRow + d.dy;
      if (this._isWalkable(nc, nr)) {
        this._lastDir = d;
        this._tweenToCell(nc, nr, SHADOW_WALKER_CHASE_TWEEN_MS);
        return;
      }
    }
  }

  /**
   * Get the walkable cardinal neighbour that is furthest from the player.
   * Used during retreat to move away.
   * @private
   * @returns {{ col: number, row: number }|null}
   */
  _getFurthestNeighbourFromPlayer() {
    if (!this._playerRef) return null;

    const neighbours = this._getWalkableNeighbours(this.gridCol, this.gridRow);
    if (neighbours.length === 0) return null;

    let best = null;
    let bestDist = -1;

    for (const n of neighbours) {
      const dist = Math.abs(n.col - this._playerRef.gridCol) +
                   Math.abs(n.row - this._playerRef.gridRow);
      if (dist > bestDist) {
        bestDist = dist;
        best = n;
      }
    }

    return best;
  }

  /**
   * Get walkable cardinal neighbours of a cell.
   * @private
   * @param {number} col
   * @param {number} row
   * @returns {Array<{col:number,row:number}>}
   */
  _getWalkableNeighbours(col, row) {
    const result = [];
    for (const d of CARDINAL) {
      const nc = col + d.dx;
      const nr = row + d.dy;
      if (this._isWalkable(nc, nr)) {
        result.push({ col: nc, row: nr });
      }
    }
    return result;
  }

  /**
   * BFS shortest path from (startCol,startRow) to (goalCol,goalRow).
   * Returns array of {col,row} steps (excluding start, including goal),
   * or null if no path found. Capped at 60 explored cells to avoid
   * expensive searches on large mazes.
   * @private
   */
  _bfsPath(startCol, startRow, goalCol, goalRow) {
    if (!this._grid) return null;

    const key = (c, r) => (c << 16) | (r & 0xffff);
    const visited = new Set();
    const parent = new Map();
    const queue = [{ col: startCol, row: startRow }];
    visited.add(key(startCol, startRow));

    let explored = 0;
    const maxExplore = 60;

    while (queue.length > 0 && explored < maxExplore) {
      const cur = queue.shift();
      explored++;

      if (cur.col === goalCol && cur.row === goalRow) {
        // Reconstruct path
        const path = [];
        let node = cur;
        while (parent.has(key(node.col, node.row))) {
          path.push(node);
          node = parent.get(key(node.col, node.row));
        }
        path.reverse();
        return path;
      }

      for (const d of CARDINAL) {
        const nc = cur.col + d.dx;
        const nr = cur.row + d.dy;
        const k = key(nc, nr);
        if (!visited.has(k) && this._isWalkable(nc, nr)) {
          visited.add(k);
          parent.set(k, cur);
          queue.push({ col: nc, row: nr });
        }
      }
    }

    return null;
  }

  /**
   * Check if a grid cell is walkable.
   * @private
   */
  _isWalkable(col, row) {
    if (!this._grid) return false;
    if (row < 0 || row >= this._grid.length) return false;
    if (col < 0 || col >= this._grid[0].length) return false;
    return this._grid[row][col] === CellType.PATH;
  }

  // ---------------------------------------------------------------
  // Private — Detection
  // ---------------------------------------------------------------

  /**
   * Check line-of-sight to the player (straight line, no walls blocking).
   * @private
   * @returns {boolean}
   */
  _checkLineOfSight() {
    if (!this._playerRef) return false;

    const pCol = this._playerRef.gridCol;
    const pRow = this._playerRef.gridRow;

    // Manhattan distance check first
    const dist = Math.abs(this.gridCol - pCol) + Math.abs(this.gridRow - pRow);
    if (dist > SHADOW_WALKER_VISION_RANGE * 2) return false;

    // Must be on the same row or column (straight line of sight)
    if (this.gridCol !== pCol && this.gridRow !== pRow) return false;

    // Check for walls between shadow and player
    if (this.gridCol === pCol) {
      const minR = Math.min(this.gridRow, pRow);
      const maxR = Math.max(this.gridRow, pRow);
      if (maxR - minR > SHADOW_WALKER_VISION_RANGE * 2) return false;
      for (let r = minR + 1; r < maxR; r++) {
        if (this._grid[r][this.gridCol] !== CellType.PATH) return false;
      }
      return true;
    }

    // Same row — check horizontal
    const minC = Math.min(this.gridCol, pCol);
    const maxC = Math.max(this.gridCol, pCol);
    if (maxC - minC > SHADOW_WALKER_VISION_RANGE * 2) return false;
    for (let c = minC + 1; c < maxC; c++) {
      if (this._grid[this.gridRow][c] !== CellType.PATH) return false;
    }
    return true;
  }

  /**
   * Check if the shadow is touching the player. On contact, deals damage
   * and steals coins, then enters retreat state with a hit cooldown so it
   * cannot stunlock the player. The shadow does NOT despawn.
   * @private
   * @param {boolean} isGhostMode
   * @returns {{ hit: boolean, damage: number, coinsStolen: number }}
   */
  _checkPlayerContact(isGhostMode) {
    if (!this._playerRef) return { hit: false, damage: 0, coinsStolen: 0 };

    // Still on cooldown from last hit
    if (this._hitCooldown > 0) return { hit: false, damage: 0, coinsStolen: 0 };

    const dx = this._playerRef.x - this.x;
    const dy = this._playerRef.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const hitDist = TILE_SIZE * 0.7;

    if (dist < hitDist) {
      // Ghost mode: shadow passes through harmlessly
      if (isGhostMode) return { hit: false, damage: 0, coinsStolen: 0 };

      // Start hit cooldown and retreat
      this._hitCooldown = SHADOW_WALKER_HIT_COOLDOWN;
      this._retreatCellsLeft = SHADOW_WALKER_RETREAT_CELLS;
      this._state = ShadowState.RETREATING;
      this._chasePath = null;

      try {
        this.scene.sound.play(AUDIO_SHADOW_WALKER_HIT, { volume: getSfxVol(0.5) });
      } catch (_) { /* audio fallback */ }

      return {
        hit: true,
        damage: SHADOW_WALKER_DAMAGE,
        coinsStolen: SHADOW_WALKER_COIN_STEAL,
      };
    }

    return { hit: false, damage: 0, coinsStolen: 0 };
  }

  // ---------------------------------------------------------------
  // Private — Visual helpers
  // ---------------------------------------------------------------

  /**
   * Draw glowing eyes on the shadow, scaled to current sprite state.
   * Eyes bob gently and track the shadow's movement direction.
   * @private
   */
  _drawEyes() {
    if (!this._eyeGfx || !this.active) return;
    this._eyeGfx.clear();

    // Subtle floating bob (1px amplitude)
    const bob = Math.sin((this._bobTimer / 600) + this._bobOffset) * 1.0;

    const s = SHADOW_WALKER_SIZE;
    const eyeY = this.y - s * 0.14 + bob;
    const eyeSpacing = s * 0.14;
    const eyeSize = 1.8;

    // During cooldown, eyes flicker to indicate the shadow is recovering
    const cooldownAlpha = this._hitCooldown > 0
      ? 0.35 + 0.5 * Math.abs(Math.sin(this._bobTimer / 80))
      : 1.0;

    // During return, dim the eyes slightly to show disengagement
    const returnDim = this._state === ShadowState.RETURNING ? 0.6 : 1.0;
    const eyeAlpha = cooldownAlpha * returnDim;

    // Outer glow
    this._eyeGfx.fillStyle(COLOR_SHADOW_WALKER_EYES, 0.35 * eyeAlpha);
    this._eyeGfx.fillCircle(this.x - eyeSpacing, eyeY, eyeSize + 1.0);
    this._eyeGfx.fillCircle(this.x + eyeSpacing, eyeY, eyeSize + 1.0);

    // Main eye
    this._eyeGfx.fillStyle(COLOR_SHADOW_WALKER_EYES, 0.85 * eyeAlpha);
    this._eyeGfx.fillCircle(this.x - eyeSpacing, eyeY, eyeSize);
    this._eyeGfx.fillCircle(this.x + eyeSpacing, eyeY, eyeSize);

    // Bright core
    this._eyeGfx.fillStyle(0xcc44ff, 1.0 * eyeAlpha);
    this._eyeGfx.fillCircle(this.x - eyeSpacing, eyeY, 0.7);
    this._eyeGfx.fillCircle(this.x + eyeSpacing, eyeY, 0.7);
  }

  /**
   * Clean up extra graphics.
   * @private
   */
  _cleanup() {
    if (this._eyeGfx) {
      this._eyeGfx.destroy();
      this._eyeGfx = null;
    }
    this._playerRef = null;
    this._grid = null;
    this._chasePath = null;
    this._returnPath = null;
  }
}
